import React from 'react';
import PropTypes from 'prop-types';
import {
  FDBox,
  FDTitleText,
  FDAccordionGroup,
  FDAccordion,
  FDAccordionTitle,
  FDAccordionDetails,
  FDStack,
  FDHeadlineText,
  FDContentContainer,
  FDAlert,
  FDTooltip,
  FDIcon,
} from '@facet-wealth/design';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { buildFacetDesignOverride } from 'Packages/FacetRest/FacetDesignOverride';
import AvailableResourcesGrossMonthlyIncomeSection from './GrossMonthlyIncomeSection/Content';
import AvailableResourcesPayrollDeductionsSection from './PayrollDeductionsSection/Content';
import AvailableResourcesEstimatedTaxesSection from './EstimatedTaxesSection/Content';
import AvailableResourcesEstimatedPaycheckSection from './EstimatedPaycheckSection/Content';
import AvailableResourcesDebtMinPaymentsSection from './DebtMinPaymentsSection/Content';
import AvailableResourcesMonthlyExpensesSection from './MonthlyExpensesSection/Content';
import AvailableAmountToSaveSection from './AvailableAmountToSaveSection/Content';

function AvailableResourcesSectionTitle({
  label, value, isSubtotal = false, hasAverage12MonthsTooltip = false,
}) {
  const size = isSubtotal ? 'lg' : 'md';

  const labelSection = hasAverage12MonthsTooltip ? (
    <FDStack spacing={1}>
      <FDTitleText value={label} size={size} />
      <FDTooltip title="Note that this value was calculated using the average monthly value over the next 12 months.">
        <FDIcon
          alignSelf="center"
          color="primary"
          icon="info"
          size="md"
        />
      </FDTooltip>
    </FDStack>
  ) : (
    <FDTitleText value={label} size={size} />
  );

  return (
    <FDStack justifyContent="space-between" flexGrow={1}>
      {labelSection}
      <FDTitleText value={value} size={size} />
    </FDStack>
  );
}

AvailableResourcesSectionTitle.defaultProps = {
  isSubtotal: false,
  hasAverage12MonthsTooltip: false,
};

AvailableResourcesSectionTitle.propTypes = {
  isSubtotal: PropTypes.bool,
  hasAverage12MonthsTooltip: PropTypes.bool,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};


const AvailableResourcesDisclaimerAlert = ({
  label, value,
}) => {
  return (
    <FDAlert
      title={label}
      description={value}
      status="warning"
    />
  );
};

AvailableResourcesDisclaimerAlert.defaultProps = {
};

AvailableResourcesDisclaimerAlert.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

const metaOverrideSet = buildFacetDesignOverride()
  .nameOverride('title', {
    _component: () => FDHeadlineText,
    _customProps: { size: 'sm' },
  }, {
    ordinal: 200,
  })
  .nameOverride(['payrollDeductionsTotal', 'estimatedTaxesTotal', 'debtMinPaymentsTotal'], {
    _component: () => AvailableResourcesSectionTitle,
  }, {
    ordinal: 200,
  })
  .nameOverride(['grossMonthlyIncomeTotal', 'expensesTotal'], {
    _component: () => AvailableResourcesSectionTitle,
    // displays a tooltip for the average 12 months
    _customProps: { hasAverage12MonthsTooltip: true },
  }, {
    ordinal: 200,
  })
  .nameOverride(['remainingNetIncomeTotal', 'estimatedPaycheckTotal'], {
    _component: () => AvailableResourcesSectionTitle,
    // isSubtotal is used to offset the remainingNetIncome to align with the accordion titles
    _customProps: { isSubtotal: true },
  }, {
    ordinal: 200,
  })
  .nameOverride('availableResourcesDisclaimer', {
    _component: () => AvailableResourcesDisclaimerAlert,
  }, { ordinal: 200 });

const AvailableResourcesLayout = () => {
  const { resource } = useMetaLayoutContext();

  return (
    <FDContentContainer maxWidth={'sm'}>
      <FDStack direction="column" spacing={2}>
        <MetaControlField attr="title" />
        <FDStack
          direction="column"
          spacing={4}
        >
          <FDStack direction="column" spacing={2}>
            <FDBox>
              <FDAccordionGroup>
                <FDAccordion expansionID="grossMonthlyIncome">
                  <FDAccordionTitle>
                    <MetaControlField attr="grossMonthlyIncomeTotal" />
                  </FDAccordionTitle>
                  <FDAccordionDetails>
                    <AvailableResourcesGrossMonthlyIncomeSection resource={resource} />
                  </FDAccordionDetails>
                </FDAccordion>
                <FDAccordion expansionID="payrollDeductions">
                  <FDAccordionTitle>
                    <MetaControlField attr="payrollDeductionsTotal" />
                  </FDAccordionTitle>
                  <FDAccordionDetails>
                    <AvailableResourcesPayrollDeductionsSection resource={resource} />
                  </FDAccordionDetails>
                </FDAccordion>
                <FDAccordion expansionID="estimatedTaxes">
                  <FDAccordionTitle>
                    <MetaControlField attr="estimatedTaxesTotal" />
                  </FDAccordionTitle>
                  <FDAccordionDetails>
                    <AvailableResourcesEstimatedTaxesSection resource={resource} />
                  </FDAccordionDetails>
                </FDAccordion>
              </FDAccordionGroup>
            </FDBox>
            <FDAccordionGroup>
              <FDAccordion expansionID="estimatedPaycheck" data-test="facet-plan-available-resources-paycheck-accordion">
                <FDAccordionTitle>
                  <MetaControlField attr="estimatedPaycheckTotal" />
                </FDAccordionTitle>
                <FDAccordionDetails>
                  <AvailableResourcesEstimatedPaycheckSection resource={resource} />
                </FDAccordionDetails>
              </FDAccordion>
            </FDAccordionGroup>
          </FDStack>
          <FDStack direction="column" spacing={2}>
            <FDBox>
              <FDAccordionGroup>
                <FDAccordion expansionID="debtMinPayments" data-test="facet-plan-debt-min-payments-accordion">
                  <FDAccordionTitle>
                    <MetaControlField attr="debtMinPaymentsTotal" />
                  </FDAccordionTitle>
                  <FDAccordionDetails>
                    <AvailableResourcesDebtMinPaymentsSection resource={resource} />
                  </FDAccordionDetails>
                </FDAccordion>
                <FDAccordion expansionID="monthlyExpenses" data-test="facet-plan-available-resources-expenses-accordion">
                  <FDAccordionTitle>
                    <MetaControlField attr="expensesTotal" />
                  </FDAccordionTitle>
                  <FDAccordionDetails>
                    <AvailableResourcesMonthlyExpensesSection resource={resource} />
                  </FDAccordionDetails>
                </FDAccordion>
              </FDAccordionGroup>
            </FDBox>
            <MetaControlField attr="remainingNetIncomeTotal" />
          </FDStack>
          <AvailableAmountToSaveSection resource={resource} />
          <MetaControlField attr="availableResourcesDisclaimer" />
        </FDStack>
      </FDStack>
    </FDContentContainer>
  );
};

const AvailableResourcesSection = ({
  relationship,
}) => {
  const { pageContentCollection } = usePageContentContext();

  if (!relationship) {
    return null;
  }

  const resource = pageContentCollection
    .included()
    .findResource(relationship.data?.[0]);

  if (!resource) {
    return null;
  }

  return (
    <MetaFormInline
      padding="none"
      noIndividualSubmitButtons
      resource={resource}
      layout={AvailableResourcesLayout}
      metaOverrideSet={metaOverrideSet}
    />
  );
};

AvailableResourcesSection.defaultProps = {};

AvailableResourcesSection.propTypes = {
  relationship: PropTypes.object.isRequired,
};

export default AvailableResourcesSection;
